/**
 * Dropdown menu — built on @radix-ui/react-dropdown-menu.
 *
 * Styled for the glass theme. Exports compound components:
 *   DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
 *   DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
 */
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { forwardRef, useCallback } from "react";
import { cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from "react";
import { cn } from "@/lib/cn";
import {
  MenuSurfaceContext,
  menuSurfaceProps,
  useMenuSurface,
  type MenuSurface,
} from "./menu-surface";
import { useInsideModalSurface } from "./modal-surface-context";
import "./transitions-recipes.css";
import { useTransitionsRecipeEntry } from "./use-transitions-recipe-entry";

/**
 * Shared menu-surface recipe — Tahoe direction (approved 2026-06-10).
 *
 * One source of truth for both DropdownMenu and ContextMenu. The "menu"
 * surface role (fill/stroke/blur + radius, owned by the role contract) is
 * applied inline on the element so it pairs with the role attribute — this
 * recipe carries layout/padding and the popup marker that gives CSS-painted
 * popups the stacked material level (glass-interaction.css). Items carry
 * their own capsule radius.
 */
const menuSurfaceVariants = cva("overflow-hidden p-appkit-xs", {
  variants: {
    surface: {
      glass: "denker-popup-surface",
      // The wall card's paper (`menu-surface.ts`): no glass coat to stack.
      card: "",
    },
  },
  defaultVariants: { surface: "glass" },
});

const menuItemVariants = cva(
  "relative flex cursor-pointer select-none items-center rounded-[16px] px-appkit-sm text-appkit-body outline-none transition-colors",
  {
    variants: {
      density: {
        default: "min-h-[30px] gap-appkit-xs py-appkit-2xs leading-none",
        roomy:
          "min-h-appkit-control-icon gap-appkit-sm py-appkit-xs leading-tight",
      },
    },
    defaultVariants: {
      density: "default",
    },
  },
);

interface MenuItemContentProps {
  children: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
  trailingClassName?: string;
}

function MenuItemContent({
  children,
  icon,
  trailing,
  className,
  iconClassName,
  labelClassName,
  trailingClassName,
}: MenuItemContentProps) {
  return (
    <span
      className={cn(
        "grid w-full min-w-0 grid-cols-[1rem_minmax(0,1fr)_max-content] items-center gap-appkit-xs",
        className,
      )}
    >
      <span
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center text-current [&>svg]:h-3.5 [&>svg]:w-3.5",
          iconClassName,
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className={cn("min-w-0 truncate", labelClassName)}>{children}</span>
      <span
        className={cn(
          "flex min-h-4 min-w-4 shrink-0 items-center justify-end text-muted [&>svg]:h-3.5 [&>svg]:w-3.5",
          trailingClassName,
        )}
      >
        {trailing}
      </span>
    </span>
  );
}

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    /**
     * Where the menu portals to (default: `document.body`). A menu opened
     * from an editor's floating toolbar portals into that toolbar's own UI
     * root, so focus in the menu still counts as focus in the editor.
     */
    container?: HTMLElement | null;
    /** Its material; by default the one its surface declares. */
    surface?: MenuSurface;
  }
>(({ className, sideOffset = 4, container, surface, ...props }, ref) => {
  const insideModal = useInsideModalSurface();
  const material = useMenuSurface(surface);
  const paint = menuSurfaceProps(material, {
    nativeLevel: "root",
    nativeGroup: "menus",
  });
  const entry = useTransitionsRecipeEntry();
  const entryRef = entry.ref;
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      entryRef(node);
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [entryRef, ref],
  );
  return (
    // Radix's presence measures the content itself: the provider wraps the
    // portal, and context follows it through.
    <MenuSurfaceContext.Provider value={material}>
      <DropdownMenuPrimitive.Portal container={container}>
        <DropdownMenuPrimitive.Content
          ref={setRef}
          data-entered={entry.entered ? "true" : "false"}
          sideOffset={sideOffset}
          // Desktop click-through hit-tests data-interactive ancestors
          // (use-click-through.ts). Portalled content escapes the marked
          // panel/shell, so it must carry its own marker.
          data-interactive="true"
          // Menus triggered inside a modal-tier surface must stack above
          // it — the promoted z lives with the menu z-source in index.css.
          data-modal-menu={insideModal ? "true" : undefined}
          className={cn(
            paint.className,
            "denker-transition-menu",
            menuSurfaceVariants({ surface: material }),
            "w-max min-w-[8rem] max-w-[90vw]",
            className,
          )}
          {...paint.attributes}
          {...props}
        />
      </DropdownMenuPrimitive.Portal>
    </MenuSurfaceContext.Provider>
  );
});
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    destructive?: boolean;
    icon?: ReactNode;
    trailing?: ReactNode;
  }
>(({ className, destructive, icon, trailing, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      menuItemVariants(),
      "focus:bg-[var(--color-interactive-hover)] data-[highlighted]:bg-[var(--color-interactive-hover)] data-[state=open]:bg-[var(--color-interactive-hover)]",
      destructive
        ? "text-danger focus:text-danger-light"
        : "text-secondary focus:text-primary data-[highlighted]:text-primary data-[state=open]:text-primary",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    {icon || trailing ? (
      <MenuItemContent icon={icon} trailing={trailing}>
        {children}
      </MenuItemContent>
    ) : (
      children
    )}
  </DropdownMenuPrimitive.Item>
));
DropdownMenuItem.displayName = "DropdownMenuItem";

/** A menu's non-interactive group header: its layout and muted mini type. */
const menuLabelClassName =
  "flex items-baseline gap-appkit-2xs px-appkit-sm pt-appkit-xs pb-0.5 text-appkit-mini text-muted";

/**
 * Non-interactive group header. Carries the header's layout and muted mini
 * type; callers style the label/hint spans inside it.
 */
const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(menuLabelClassName, className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("mx-0 my-appkit-2xs h-px bg-glass-stroke-subtle", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  menuSurfaceVariants,
  menuItemVariants,
  menuLabelClassName,
  MenuItemContent,
};
export type { MenuItemContentProps };
